import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaUserGraduate, 
  FaBuilding, 
  FaIdCard, 
  FaEnvelope, 
  FaCalendarAlt, 
  FaEdit, 
  FaMedal, 
  FaChartLine,
  FaMagic,
  FaTrophy,
  FaCertificate,
  FaCheckCircle,
  FaRocket
} from "react-icons/fa";
import AvatarModal from "../BasicComponents/AvatarModal";
import API_BASE_URL from "../Config/config";

const getBadgeImage = (rank = "") => {
  const imageMap = {
    Apprentice: "/apprentice.png",
    Explorer:   "/explorer.png",
    Adept:      "/adept.png",
    Proficient: "/proficient.png",
    Specialist: "/specialist.png",
    Expert:     "/expert.png",
    Mastermind: "/mastermind.png",
  };
  return imageMap[rank] || "/default.png";
};

const resolveStudentData = (u = {}) => ({
  userId:     u.userId     || u.id        || u.user_id     || null,
  name:       u.name       || u.full_name || u.fullName
              || (u.first_name
                  ? `${u.first_name} ${u.last_name || ""}`.trim()
                  : null),
  rollNumber: u.roll_number || u.rollNumber || u.roll_no || null,
  branch:     u.branch      || u.department || null,
  email:      u.email       || null,
  semester:   u.semester    || null,
  collegeName:u.college_name|| u.collegeName || null,
});

const val = (v) => (v && String(v).trim() ? String(v).trim() : "Not Available");

const StatCard = ({ icon, label, value, color }) => (
  <div className="premium-card p-5 flex flex-col items-center text-center gap-2">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg mb-1 shadow-sm" style={{ backgroundColor: `${color}10`, color }}>
      {icon}
    </div>
    <p className="text-[10px] font-black text-[#6B7280] uppercase tracking-widest leading-tight">{label}</p>
    <p className="text-xl font-black text-[#111827]">{value}</p>
  </div>
);

const CleanProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading]           = useState(true);
  const [studentData, setStudentData]   = useState(null);
  const [profileImage, setProfileImage] = useState("/user image.webp");
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [rankData, setRankData]         = useState(null);
  const [stats, setStats]               = useState({
    projects: 0,
    topics: 0,
    streak: "0 Days",
    xp: "0"
  });

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "null");
    const savedAvatar = localStorage.getItem("selectedAvatar");

    if (storedUser) {
      const data = resolveStudentData(storedUser);
      setStudentData(data);
      if (savedAvatar) setProfileImage(savedAvatar);
      else if (storedUser.profile_image) setProfileImage(storedUser.profile_image);

      if (data.userId) {
        fetch(`${API_BASE_URL}/user-ranking?user_id=${data.userId}`)
          .then((r) => r.json())
          .then((d) => setRankData(d))
          .catch(() => {});
      }

      // Calculate dynamic stats
      const calculateStats = async () => {
        let totalMastered = 0;
        const techs = ['AI', 'AWS', 'React', 'java', 'python', 'java-premium', 'python-premium'];
        techs.forEach(tech => {
          const completed = JSON.parse(localStorage.getItem(`completed_docs_${tech}`) || "[]");
          totalMastered += completed.length;
        });

        // Fetch Bootcamp projects/enrollments
        let bootcampCount = 0;
        const userId = storedUser?.userId || storedUser?.id || storedUser?.user_id;
        if (userId) {
          try {
            const res = await fetch(`${API_BASE_URL}/api/bootcamp/enrollments/my-bootcamps?userId=${userId}`);
            const data = await res.json();
            if (data && data.enrollments) bootcampCount = data.enrollments.length;
          } catch (e) {}
        }

        setStats({
          projects: bootcampCount,
          topics: totalMastered,
          streak: "5 Days", // Static for now as we don't track login streaks
          xp: (totalMastered * 50 + bootcampCount * 500).toLocaleString()
        });
      };

      calculateStats();
    }
    setTimeout(() => setLoading(false), 300);
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#111827] font-sans pb-12">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-8">
        
        {/* Header */}
        <div className="flex items-center justify-between animate-fade-in">
          <button onClick={() => navigate(-1)} className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm">
            <FaArrowLeft /> Back
          </button>
          <button onClick={() => navigate("/edit-profile")} className="btn-primary px-6 py-2 flex items-center gap-2 text-sm">
            <FaEdit /> Edit Profile
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left: Identity Card */}
          <div className="lg:col-span-4 space-y-6 animate-slide-up">
            <div className="premium-card p-8 flex flex-col items-center text-center relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#5B21B6] to-[#A855F7]"></div>
              
              <div className="relative mb-6">
                {/* Premium Gradient Ring */}
                <div className="absolute -inset-2 bg-gradient-to-tr from-[#5B21B6] via-[#7C3AED] to-[#A855F7] rounded-full animate-spin-slow opacity-75 blur-[2px]"></div>
                <div className="absolute -inset-1 bg-white rounded-full"></div>
                <div className="relative w-32 h-32 rounded-full overflow-hidden shadow-2xl ring-4 ring-white bg-white">
                   <img 
                    src={profileImage} 
                    alt="Profile" 
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                   />
                   <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <FaMagic className="text-white text-xl" />
                   </div>
                </div>
                <button 
                  onClick={() => setShowAvatarModal(true)}
                  className="absolute bottom-1 right-1 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-[#5B21B6] hover:bg-purple-50 transition-colors z-10"
                >
                  <FaEdit size={12} />
                </button>
              </div>

              <div className="w-full text-left">
                <h2 className="text-2xl font-black text-[#111827] mb-1">{val(studentData?.name)}</h2>
                <p className="text-xs font-black text-[#5B21B6] uppercase tracking-[3px] mb-6">Learner Ecosystem</p>
              </div>
              
              <div className="w-full space-y-4 text-left">
                {[
                  { icon: <FaUserGraduate />, label: "Roll Number", value: studentData?.rollNumber },
                  { icon: <FaBuilding />, label: "Department", value: studentData?.branch },
                  { icon: <FaEnvelope />, label: "Email", value: studentData?.email },
                  { icon: <FaIdCard />, label: "Unique ID", value: studentData?.userId },
                ]
                .filter(item => item.value && String(item.value).trim() !== "")
                .map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-[#F8FAFC] border-2 border-[#5B21B6]">
                    <div className="text-[#5B21B6] opacity-70">{item.icon}</div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-[#6B7280] uppercase tracking-widest">{item.label}</p>
                      <p className="text-xs font-bold text-[#111827] truncate">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* XP Level Card */}
            <div className="premium-card p-8 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] text-white border-2 border-[#5B21B6] shadow-premium relative overflow-hidden">
               <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12 blur-2xl"></div>
               <div className="relative z-10 space-y-6">
                 <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[2px] opacity-80">Current Level</p>
                      <h3 className="text-4xl font-black">Lvl {rankData?.level || 1}</h3>
                    </div>
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl backdrop-blur-sm border border-white/20">
                      <FaRocket />
                    </div>
                 </div>
                 
                 <div className="space-y-2">
                   <div className="flex justify-between text-xs font-bold">
                      <span>Experience</span>
                      <span>{rankData?.xp || 0} / 2100 XP</span>
                   </div>
                   <div className="h-3 w-full bg-white/20 rounded-full overflow-hidden border border-white/10">
                      <div className="h-full bg-white rounded-full shadow-[0_0_15px_rgba(255,255,255,0.4)]" style={{ width: `${Math.min(((rankData?.xp || 0) / 2100) * 100, 100)}%` }}></div>
                   </div>
                 </div>
               </div>
            </div>
          </div>

          {/* Right: Statistics & Badges */}
          <div className="lg:col-span-8 space-y-8 animate-slide-up" style={{ animationDelay: '100ms' }}>
            
            {/* Rank Badge Section */}
            <div className="premium-card p-8 flex flex-col md:flex-row items-center gap-8 relative overflow-hidden">
               <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-purple-50 to-transparent pointer-events-none"></div>
               <div className="flex-1 space-y-4">
                  <h3 className="text-xl font-black text-[#111827] flex items-center gap-3">
                    <span className="p-2 rounded-lg bg-purple-50 text-[#5B21B6]"><FaMedal /></span>
                    Achievement Summary
                  </h3>
                  <div className="space-y-1">
                    <p className="text-xs font-black text-[#6B7280] uppercase tracking-widest">Active Title</p>
                    <p className="text-2xl font-black text-[#5B21B6] italic">"{rankData?.title || 'Aspiring Architect'}"</p>
                  </div>
                  <p className="text-sm text-[#6B7280] font-medium leading-relaxed">
                    Track your growth through levels and XP. Keep pushing to unlock new ranks and prestigious titles!
                  </p>
               </div>
               <div className="flex-shrink-0 relative group">
                  <div className="absolute inset-0 bg-purple-200 rounded-full blur-2xl opacity-40 group-hover:opacity-60 transition-opacity"></div>
                  <img 
                    src={getBadgeImage(rankData?.rank)} 
                    alt="Rank Badge" 
                    className="w-40 h-40 object-contain relative z-10 transition-transform duration-500 hover:scale-110 drop-shadow-xl"
                  />
               </div>
            </div>

            {/* Dynamic Learning Statistics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard 
                icon={<FaRocket />} 
                label="Bootcamps" 
                value={stats.projects} 
                color="#5B21B6" 
              />
              <StatCard 
                icon={<FaChartLine />} 
                label="Topics Mastered" 
                value={stats.topics} 
                color="#7C3AED" 
              />
              <StatCard 
                icon={<FaTrophy />} 
                label="Streak" 
                value={stats.streak} 
                color="#F59E0B" 
              />
              <StatCard 
                icon={<FaCheckCircle />} 
                label="XP Points" 
                value={stats.xp} 
                color="#10B981" 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Avatar Modal */}
      {showAvatarModal && (
        <AvatarModal
          onClose={() => setShowAvatarModal(false)}
          onSelect={(avatarPath) => {
            setProfileImage(avatarPath);
            localStorage.setItem("selectedAvatar", avatarPath);
            setShowAvatarModal(false);
          }}
        />
      )}
    </div>
  );
};

export default CleanProfile;


